import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, ApiError, setAccessToken, setRefreshHandler } from '../api/client';
import { queryPersister } from '../offline/persister';
import {
  clearLocalData,
  clearSessionProfile,
  readSessionProfile,
  saveSessionProfile,
} from '../offline/db';
import { demoUser } from '../data/demo';
import type { Usuario } from '../api/types';

type AuthStatus = 'loading' | 'ready';

interface AuthContextValue {
  user: Usuario | null;
  status: AuthStatus;
  demo: boolean;
  login: (identifier: string, password: string) => Promise<void>;
  demoLogin: () => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextValue | null>(null);
const DEMO_KEY = 'gravenca.demo';
const SESSION_HINT_KEY = 'gravenca.session-hint';

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<Usuario | null>(null);
  const [demo, setDemo] = useState(false);
  const [status, setStatus] = useState<AuthStatus>('loading');
  const refreshInFlight = useRef<Promise<boolean> | null>(null);

  /**
   * Single-flight: StrictMode, varias pestañas o una ráfaga de 401 no deben
   * canjear el mismo refresh token dos veces (el servidor lo rota y el segundo
   * intento quedaría inválido).
   */
  const refresh = useCallback(async (): Promise<boolean> => {
    if (refreshInFlight.current) return refreshInFlight.current;

    const inFlight = (async () => {
      try {
        const { data } = await api.refresh();
        setAccessToken(data.accessToken);
        setUser(data.user);
        setDemo(false);
        window.localStorage.setItem(SESSION_HINT_KEY, '1');
        await saveSessionProfile(data.user).catch(() => undefined);
        window.dispatchEvent(new Event('erp:session-restored'));
        return true;
      } catch (error) {
        setAccessToken(null);

        // Sin red o servidor caído: se reabre la app con el perfil cacheado y
        // los datos de IndexedDB. No hay token: las mutaciones van a la cola.
        if (error instanceof ApiError && error.status === 0) {
          const profile = await readSessionProfile().catch(() => null);
          if (profile) {
            setUser(profile);
            setDemo(false);
            return true;
          }
        } else if (error instanceof ApiError && error.status === 401) {
          window.localStorage.removeItem(SESSION_HINT_KEY);
          await clearSessionProfile().catch(() => undefined);
        }
        return false;
      }
    })();

    refreshInFlight.current = inFlight;
    try {
      return await inFlight;
    } finally {
      refreshInFlight.current = null;
    }
  }, []);

  useEffect(() => {
    setRefreshHandler(refresh);
    return () => setRefreshHandler(null);
  }, [refresh]);

  useEffect(() => {
    let cancelled = false;

    if (window.localStorage.getItem(DEMO_KEY) === '1') {
      setUser(demoUser);
      setDemo(true);
      setStatus('ready');
      return;
    }

    // Sin indicio de sesión previa no se golpea /api/auth/refresh: menos ruido
    // de 401 y arranque más rápido para visitantes nuevos.
    if (window.localStorage.getItem(SESSION_HINT_KEY) !== '1') {
      setUser(null);
      setStatus('ready');
      return;
    }

    void refresh().then((ok) => {
      if (cancelled) return;
      if (!ok) setUser(null);
      setStatus('ready');
    });

    return () => {
      cancelled = true;
    };
  }, [refresh]);

  const resetRemoteState = useCallback(async () => {
    setAccessToken(null);
    setUser(null);
    setDemo(false);
    queryClient.clear();
    await queryPersister.removeClient();
    await clearLocalData();
  }, [queryClient]);

  const login = useCallback(
    async (identifier: string, password: string) => {
      const { data } = await api.login(identifier, password);
      window.localStorage.removeItem(DEMO_KEY);
      window.localStorage.setItem(SESSION_HINT_KEY, '1');
      setAccessToken(data.accessToken);
      setUser(data.user);
      setDemo(false);
      setStatus('ready');
      await saveSessionProfile(data.user).catch(() => undefined);
      window.dispatchEvent(new Event('erp:session-restored'));
    },
    [],
  );

  const demoLogin = useCallback(async () => {
    await resetRemoteState();
    window.localStorage.removeItem(SESSION_HINT_KEY);
    window.localStorage.setItem(DEMO_KEY, '1');
    setUser(demoUser);
    setDemo(true);
    setStatus('ready');
  }, [resetRemoteState]);

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } catch {
      // la sesión local se cierra igual
    }
    window.localStorage.removeItem(DEMO_KEY);
    window.localStorage.removeItem(SESSION_HINT_KEY);
    await resetRemoteState();
    setStatus('ready');
  }, [resetRemoteState]);

  const value = useMemo(
    () => ({ user, status, demo, login, demoLogin, logout, refresh }),
    [user, status, demo, login, demoLogin, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth debe usarse dentro de AuthProvider');
  return context;
}
